import React, { useState, useEffect } from 'react';
import { useQueryParam, NumberParam } from 'use-query-params';
import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import { Pagination } from '@mui/material';
import Container from '../../components/Container';
import Page from '../../components/Page';
import Offers from '../../components/offers/Offers';
import CreateAlert from './components/CreateAlert';
import CancelIcon from '@mui/icons-material/Cancel';
import SearchSummary from 'components/InteractiveSearch/SearchSummary';
import useAssistedSearch from 'components/InteractiveSearch/useAssistedSearch';
import { buildSearchScope, newSearchParams, viewportFromSearchParams } from 'utils/searchScope';
import { usePublishSearchViewport } from 'components/InteractiveSearch/SearchViewportContext';
import { useLocation } from 'utils/location';
import TopOffers from 'modules/LandingPage/components/TopOffers';
import MapView from '../../components/InteractiveSearch/MapView';
import ListView from '../../components/InteractiveSearch/ListView';
import { filterMapOffers, filterOffers } from 'utils/offerFilters';
import useOfferDisplayMode from 'components/offers/useOfferDisplayMode';
import { offerFilters } from 'components/InteractiveSearch/offerFilterParams';

const getBusinessName = (offers) => get(offers, 'content[0].businessName');

const SearchPageContainer = () => {
  const userLocation = useLocation();
  const [expireSoon] = useQueryParam('expireSoon');
  const [local] = useQueryParam('local');
  const [sort] = useQueryParam('sort');
  const [radius] = useQueryParam('radius');
  const [query=''] = useQueryParam('query');
  const [selectedCategory, setSelectedCategory] = useQueryParam('category');
  const [businessId, setBusinessId] = useQueryParam('businessId');
  const [currentPage, setCurrentPage] = useQueryParam('currentPage', NumberParam);
  const [businessName] = useQueryParam('businessName');
  const [searchParams, setSearchParams] = useSearchParams();
  const [listMode] = useOfferDisplayMode(searchParams.get('display'));
  const [isMapVisible, setIsMapVisible] = useState(false);
  const [isListVisible, setIsListVisible] = useState(false);
  const [highlightedOfferId, setHighlightedOfferId] = useState(null);
  const [initialMapViewport] = useState(() => viewportFromSearchParams(searchParams));
  const [mapViewport, setMapViewport] = useState(initialMapViewport);
  const { lat, lng } = useSelector((s) => ({
    lat: get(s, 'user.location.latitude'),
    lng: get(s, 'user.location.longitude'),
  }));

  // Sync list/display state from URL params (controlled by header buttons)
  useEffect(() => {
    const listParam = searchParams.get('list');
    const mapParam = searchParams.get('map');

    if (listParam === '1' || listParam === '0') {
      setIsListVisible(listParam === '1');
    }

    if (mapParam === '1' || mapParam === '0') {
      setIsMapVisible(mapParam === '1');
    }
  }, [searchParams]);

  usePublishSearchViewport(mapViewport);
  const ordinaryParams = new URLSearchParams(searchParams);
  ordinaryParams.delete('map');
  const ordinaryScope = buildSearchScope(ordinaryParams, userLocation);
  const mapScope = buildSearchScope(searchParams, userLocation, mapViewport);
  const autoCorrect = searchParams.get('autoCorrect') !== 'false';
  const { data: offers, error } = useAssistedSearch(
    userLocation ? ordinaryScope : null, query, ((currentPage || 1) - 1) * 12, 12, autoCorrect,
  );
  const { data: mapOffersResponse } = useAssistedSearch(
    isMapVisible && mapViewport && userLocation ? mapScope : null, query, 0, 50, autoCorrect,
  );

  if (error) {
    return (
      <Page>
        <Helmet>
          <title>Search offers - HiddenUnderGround</title>
        </Helmet>
        <Container>
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <Typography variant="h6" color="error">
              Something went wrong
            </Typography>
          </Box>
        </Container>
      </Page>
    );
  }

  if (!offers) {
    return (
      <Page>
        <Helmet>
          <title>Search offers - HiddenUnderGround</title>
        </Helmet>
        <Container>
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Container>
      </Page>
    );
  }

  // Determine selected filter type
  const getSelectedFilter = () => {
    if (expireSoon === 'true') return offerFilters.expireSoon;
    if (local === 'true' && sort === 'distance') return offerFilters.nearMe;
    if (local === 'true') return offerFilters.local;
    return offerFilters.none;
  };

  // User radius from URL for optional frontend cap; null trusts backend per-offer radius
  let currentRadius = null;
  if (radius != null) {
    const parsed =
      typeof radius === 'string'
        ? parseFloat(radius)
        : Number(radius);
    currentRadius = Number.isFinite(parsed) ? parsed : null;
  }
  
  // The ordinary results keep the user's selected filters. Map results are loaded separately
  // from the current viewport so they are not capped by the user's local service radius.
  const selectedFilter = getSelectedFilter();
  const shouldApplyFilter = selectedFilter !== offerFilters.none;
  
  const filteredCategoryOffersRaw = shouldApplyFilter 
    ? filterOffers(offers.content || [], selectedFilter, false, currentRadius)
    : offers.content;

  const filteredCategoryOffers = (filteredCategoryOffersRaw || []).map((offer, idx) => ({
    ...offer,
    displayIndex: idx + 1,
  }));

  const mapOffersSource = isMapVisible ? (mapOffersResponse?.content || []) : offers.content || [];
  const displayedResponse = isMapVisible ? mapOffersResponse : offers;
  const visibleMapOffers = filterMapOffers(mapOffersSource, mapViewport?.bounds).map((offer, idx) => ({
    ...offer,
    displayIndex: idx + 1,
  }));
  const searchOriginalQuery = () => {
    const params = newSearchParams(searchParams, query);
    params.set('autoCorrect', 'false');
    setSearchParams(params);
  };
  return (
    <Page>
      <Helmet>
        <title>Search offers - HiddenUnderGround</title>
      </Helmet>
      <Container>
        <SearchSummary response={displayedResponse} query={query} onOriginalSearch={searchOriginalQuery} />

        {/* Map and List Views controlled by header buttons */}
        {(isMapVisible || isListVisible) && (visibleMapOffers.length > 0 || isMapVisible) && (
          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 0, md: 2 },
                flexDirection: { xs: 'column', md: (isMapVisible && isListVisible) ? 'row' : 'column' },
              }}
            >
              {/* Map View */}
              {isMapVisible && (
                <Box
                  sx={{
                    flex: (isMapVisible && isListVisible) ? 1 : 'auto',
                    width: { xs: '100%', md: (isMapVisible && isListVisible) ? '50%' : '100%' },
                    order: { xs: 1, md: (isMapVisible && isListVisible) ? 'unset' : 1 },
                  }}
                >
                  <MapView
                    offers={visibleMapOffers}
                    onMarkerClick={(offerId) => {
                      setHighlightedOfferId(offerId);
                      if (!isListVisible) {
                        // This would need to be handled by URL params
                        console.log('Marker clicked, should show list');
                      }
                    }}
                    highlightedOfferId={highlightedOfferId}
                    userLocation={userLocation}
                    isVisible={isMapVisible}
                    initialViewport={initialMapViewport}
                    viewportBounds={mapViewport?.bounds}
                    onViewportChange={setMapViewport}
                  />
                </Box>
              )}

              {/* List View */}
              {isListVisible && (
                <Box
                  sx={{
                    flex: (isMapVisible && isListVisible) ? 1 : 'auto',
                    width: (isMapVisible && isListVisible) ? '50%' : '100%',
                  }}
                >
                  <ListView
                    offers={visibleMapOffers}
                    highlightedOfferId={highlightedOfferId}
                    onOfferHover={(offerId) => setHighlightedOfferId(offerId)}
                    displayMode="list"
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {businessId && (
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography
              component="h2"
              variant="h6"
              gutterBottom
              sx={{ fontWeight: 400 }}
            >
              Selected business: {businessName || getBusinessName(offers) || `Business ID ${businessId}`}
            </Typography>
            <IconButton size="small" onClick={() => setBusinessId(undefined)}>
              <CancelIcon color="disabled" />
            </IconButton>
          </Box>
        )}
        
        {offers?.categories?.length > 0 && (
          <Box sx={{ display: 'flex', my: 2, overflow: 'auto' }}>
            {offers.categories.map((category) => (
              <Chip
                key={category}
                sx={{ mr: 2 }}
                clickable
                color={category === selectedCategory ? 'primary' : undefined}
                onClick={() => {
                  setSelectedCategory(category);
                  setCurrentPage(1);
                }}
                onDelete={
                  category === selectedCategory
                    ? () => {
                        setSelectedCategory();
                        setCurrentPage(1);
                      }
                    : undefined
                }
                label={category}
              />
            ))}
          </Box>
        )}
        
        {offers?.totalElements === 0 && (
          <CreateAlert
            alertConfig={{
              query,
              lng,
              lat,
              radius,
              expireSoon,
              selectedCategory,
              businessId,
              businessName,
            }}
          />
        )}
        
        {offers?.totalElements > 0 && (
          <>
            {isMapVisible && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="h6">All search results</Typography>
                <SearchSummary response={offers} query={query} onOriginalSearch={searchOriginalQuery} />
              </Box>
            )}
            <Offers offers={filteredCategoryOffers} offerType={listMode} columns={3} />
            <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
              <Pagination
                page={currentPage || 1}
                count={offers.totalPages}
                onChange={(e, newPage) => setCurrentPage(newPage)}
                color="primary"
              />
            </Box>
          </>
        )}
        
        <Box sx={{ my: 6 }}>
          <Typography component="h3" variant="h5" gutterBottom>
            Popular offers
          </Typography>
          <TopOffers />
        </Box>
      </Container>
    </Page>
  );
};

export default SearchPageContainer;
